import { z } from "zod";

export const InquiryStageSchema = z.enum([
  "new",
  "qualified",
  "quote_sent",
  "booked",
  "delivered",
  "closed_won",
  "closed_lost",
]);
export type InquiryStage = z.infer<typeof InquiryStageSchema>;

export const STAGES: InquiryStage[] = [
  "new",
  "qualified",
  "quote_sent",
  "booked",
  "delivered",
  "closed_won",
  "closed_lost",
];

export const STAGE_LABELS: Record<InquiryStage, string> = {
  new: "New",
  qualified: "Qualified",
  quote_sent: "Quote Sent",
  booked: "Booked",
  delivered: "Delivered",
  closed_won: "Won",
  closed_lost: "Lost",
};

export const InquiryEventTypeSchema = z.enum([
  "wedding",
  "corporate",
  "birthday",
  "private",
  "other",
  // Migration 008: the marketing site's founding.html page posts
  // membership-interest leads through the public inquiry intake — not an
  // event, so it gets its own value distinct from "other".
  "founding_membership",
  // Migration 013: Art Days pop-up sign-ups, same reasoning as
  // founding_membership above.
  "art_days",
]);
export type InquiryEventType = z.infer<typeof InquiryEventTypeSchema>;

export const EVENT_TYPES: InquiryEventType[] = [
  "wedding",
  "corporate",
  "birthday",
  "private",
  "other",
  "founding_membership",
  "art_days",
];

export const EVENT_TYPE_LABELS: Record<InquiryEventType, string> = {
  wedding: "Wedding",
  corporate: "Corporate",
  birthday: "Birthday",
  private: "Private",
  other: "Other",
  founding_membership: "Founding Membership",
  art_days: "Art Days",
};

export const InquirySourceSchema = z.enum([
  "referral",
  "instagram",
  "website",
  "thumbtack",
  "knot",
  "planner",
  "venue",
  "other",
]);
export type InquirySource = z.infer<typeof InquirySourceSchema>;

export const SOURCES: InquirySource[] = [
  "referral",
  "instagram",
  "website",
  "thumbtack",
  "knot",
  "planner",
  "venue",
  "other",
];

export const SOURCE_LABELS: Record<InquirySource, string> = {
  referral: "Referral",
  instagram: "Instagram",
  website: "Website",
  thumbtack: "Thumbtack",
  knot: "The Knot",
  planner: "Planner",
  venue: "Venue",
  other: "Other",
};

// The playbook's qualification checklist — stored as string arrays of these
// ids on the inquiry's `qualification` JSON field.
export const GREEN_FLAGS = [
  { id: "planner_involved", label: "Planner involved" },
  { id: "guests_75_150", label: "75-150 guests" },
  { id: "venue_requires_coi", label: "Venue requires COI" },
  { id: "na_questions_asked", label: "NA questions asked" },
  { id: "peak_season_saturday", label: "Peak-season Saturday" },
  { id: "something_special", label: "“Something special” language" },
] as const;

export const RED_FLAGS = [
  { id: "hourly_rate_shopping", label: "Hourly-rate shopping" },
  { id: "under_30_guests", label: "<30 guests" },
  { id: "bundled_alcohol_request", label: "Bundled alcohol request" },
  { id: "wont_pay_deposit", label: "Won't pay deposit" },
  { id: "two_hour_gig", label: "2-hour gig" },
] as const;

// LEAD-1: delivery outcome of the new-lead alert email
// (app/services/lead_alert.py::notify_new_lead, migration 011).
export const InquiryNotifyStatusSchema = z.enum(["pending", "sent", "failed"]);
export type InquiryNotifyStatus = z.infer<typeof InquiryNotifyStatusSchema>;

export const QualificationSchema = z.object({
  green_flags: z.array(z.string()),
  red_flags: z.array(z.string()),
});
export type Qualification = z.infer<typeof QualificationSchema>;

export const InquirySchema = z.object({
  id: z.string(),
  contact_id: z.string().nullable(),
  contact_name: z.string(),
  event_date: z.string().nullable(),
  event_type: InquiryEventTypeSchema.nullable(),
  guest_count: z.number().nullable(),
  venue_name: z.string().nullable(),
  na_guests_expected: z.boolean().nullable(),
  budget_band: z.string().nullable(),
  source: InquirySourceSchema.nullable(),
  qualification: QualificationSchema,
  stage: InquiryStageSchema,
  lost_reason: z.string().nullable(),
  message: z.string().nullable(),
  first_reply_at: z.string().nullable(),
  sla_due_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string().nullable().optional(),
  notify_status: InquiryNotifyStatusSchema,
  notify_error: z.string().nullable().optional(),
  // Phase 6: closes "deal #1 becomes deal #2" — set when this inquiry was
  // referred by a past event. Optional/nullable: not every backend revision
  // resolves it onto every inquiry row (mirrors invoice.ts's paid_cents
  // convention for a field a later phase bolts on).
  referral_event_id: z.string().nullable().optional(),
});
export type Inquiry = z.infer<typeof InquirySchema>;

export const InquiryListResponseSchema = z.object({
  items: z.array(InquirySchema),
  next_cursor: z.string().nullable(),
});
export type InquiryListResponse = z.infer<typeof InquiryListResponseSchema>;

// GET /api/v2/inquiries/board — a lighter-weight projection for the kanban.
export const BoardItemSchema = z.object({
  id: z.string(),
  contact_name: z.string(),
  event_date: z.string().nullable(),
  event_type: InquiryEventTypeSchema.nullable(),
  guest_count: z.number().nullable(),
  stage: InquiryStageSchema,
  sla_breached: z.boolean(),
  notify_status: InquiryNotifyStatusSchema,
});
export type BoardItem = z.infer<typeof BoardItemSchema>;

export const BoardColumnSchema = z.object({
  stage: InquiryStageSchema,
  items: z.array(BoardItemSchema),
});
export type BoardColumn = z.infer<typeof BoardColumnSchema>;

export const BoardResponseSchema = z.object({
  columns: z.array(BoardColumnSchema),
});
export type BoardResponse = z.infer<typeof BoardResponseSchema>;

// ---- Forms -----------------------------------------------------------

// "New inquiry" intake form. Only contact_name is required per the API
// contract; everything else is optional. event_type/source use an empty
// string sentinel for "not selected" (kept truly optional rather than
// defaulting to a guessed value) and are normalized to null in toApiInput.
export const InquiryFormSchema = z.object({
  contact_name: z.string().min(1, "Contact name is required"),
  event_date: z.string().optional(),
  event_type: z.union([InquiryEventTypeSchema, z.literal("")]).optional(),
  guest_count: z.string().optional(),
  venue_name: z.string().optional(),
  source: z.union([InquirySourceSchema, z.literal("")]).optional(),
  na_guests_expected: z.boolean().optional(),
  budget_band: z.string().optional(),
  message: z.string().optional(),
  // Phase 11: consent checkboxes on intake — passed straight through to the
  // inquiry create payload (POST /api/v2/inquiries + POST /api/public/inquiries
  // both accept these per contract).
  email_consent: z.boolean().optional(),
  sms_consent: z.boolean().optional(),
});
export type InquiryFormValues = z.infer<typeof InquiryFormSchema>;

export const EMPTY_INQUIRY_FORM_VALUES: InquiryFormValues = {
  contact_name: "",
  event_date: "",
  event_type: "",
  guest_count: "",
  venue_name: "",
  source: "",
  na_guests_expected: false,
  budget_band: "",
  message: "",
  email_consent: false,
  sms_consent: false,
};
